import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faGraduationCap, faChalkboardTeacher, faChess, faChild, faCalendarAlt, faBook, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { applications } from '../data/applications';
import './Applications.css';

// Import images
import applicationsHeroImage from '../assets/images/duyurular.jpg';

const Applications = () => {
  const popularApps = applications.filter(app => app.isPopular);
  const newApps = applications.filter(app => app.isNew);
  const internationalApps = applications.filter(app => app.category === 'Yurtdışı Eğitimi');
  const otherApps = applications.filter(app => !app.isPopular && !app.isNew && app.category !== 'Yurtdışı Eğitimi');

  const getIcon = (iconName) => {
    const icons = {
      academy: faGraduationCap,
      tutor: faChalkboardTeacher,
      ib: faBook,
      chess: faChess,
      kids: faChild,
      program: faCalendarAlt,
      edtech: faBook,
      publisher: faBuilding
    };
    return icons[iconName] || faGraduationCap;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "ProgenX Uygulamaları",
    "description": "ProgenX'in eğitim teknolojileri, özel ders, IB eğitimi, satranç ve çocuk eğitimi uygulamaları",
    "url": "https://progenxacademy.com/uygulamalar",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": applications.map((app, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "SoftwareApplication",
          "name": app.title,
          "description": app.description,
          "url": app.url,
          "applicationCategory": app.category,
          "operatingSystem": "Web"
        }
      }))
    }
  };

  return (
    <div className="applications">
      <Helmet>
        <title>ProgenX Uygulamaları | Eğitim Teknolojileri ve Platformlar 2025</title>
        <meta name="description" content="ProgenX'in eğitim teknolojileri uygulamaları: ProgenX Academy, Özel Ders Platformu, IB Eğitimi, ProgenX Chess, ProgenX Kids, Ders Programı ve EdTech İçerik Platformu. Yapay zeka destekli öğrenme çözümleri." />
        <meta name="keywords" content="progenx uygulamalar, progenx academy, özel ders platformu, ib eğitimi, progenx chess, progenx kids, ders programı, edtech platform, eğitim teknolojileri, yapay zeka eğitim" />
        <meta property="og:title" content="ProgenX Uygulamaları | Eğitim Teknolojileri Platformları" />
        <meta property="og:description" content="ProgenX'in eğitim teknolojileri, özel ders, IB eğitimi ve çocuk eğitimi uygulamalarını keşfedin." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/uygulamalar" />
        <link rel="canonical" href="https://progenxacademy.com/uygulamalar" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <Hero 
        title="UYGULAMALARIMIZ" 
        backgroundImage={applicationsHeroImage}
      />
      
      <section className="applications-content">
        <div className="container">
          <h2 className="section-title">Uygulamalarımız</h2>
          <p className="applications-intro">
            ProgenX olarak eğitim teknolojileri alanında çeşitli platformlar ve uygulamalar geliştiriyoruz. 
            Her biri farklı ihtiyaçlara yönelik özel olarak tasarlanmış çözümler sunuyor.
          </p>

          {popularApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Popüler Uygulamalar</h3>
              <div className="applications-grid">
                {popularApps.map(app => (
                  <div 
                    key={app.id}
                    className="application-card"
                  >
                    <a 
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-external-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Uygulamaya Git
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                    <div className="app-icon">
                      <FontAwesomeIcon icon={getIcon(app.icon)} />
                    </div>
                    <h4 className="app-title">{app.title}</h4>
                    <p className="app-description">{app.description}</p>
                    <Link 
                      to={app.id === 'progenx-academy' ? '/progenxakademi' : `/uygulamalar/${app.slug}`}
                      className="app-btn-inspect"
                    >
                      İncele
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {newApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Yeni Uygulamalar</h3>
              <div className="applications-grid">
                {newApps.map(app => (
                  <div 
                    key={app.id}
                    className="application-card"
                  >
                    <a 
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-external-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Uygulamaya Git
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                    <div className="app-icon">
                      <FontAwesomeIcon icon={getIcon(app.icon)} />
                    </div>
                    <h4 className="app-title">{app.title}</h4>
                    <p className="app-description">{app.description}</p>
                    <Link 
                      to={`/uygulamalar/${app.slug}`}
                      className="app-btn-inspect"
                    >
                      İncele
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {otherApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Diğer Uygulamalar</h3>
              <div className="applications-grid">
                {otherApps.map(app => (
                  <div 
                    key={app.id}
                    className="application-card"
                  >
                    <a 
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-external-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Uygulamaya Git
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                    <div className="app-icon">
                      <FontAwesomeIcon icon={getIcon(app.icon)} />
                    </div>
                    <h4 className="app-title">{app.title}</h4>
                    <p className="app-description">{app.description}</p>
                    <Link 
                      to={`/uygulamalar/${app.slug}`}
                      className="app-btn-inspect"
                    >
                      İncele
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {internationalApps.length > 0 && (
            <section className="applications-section">
              <h3 className="section-subtitle">Yurtdışı Eğitimi</h3>
              <div className="applications-grid">
                {internationalApps.map(app => (
                  <div 
                    key={app.id}
                    className="application-card"
                  >
                    <a 
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-external-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Uygulamaya Git
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                    <div className="app-icon">
                      <FontAwesomeIcon icon={getIcon(app.icon)} />
                    </div>
                    <h4 className="app-title">{app.title}</h4>
                    <p className="app-description">{app.description}</p>
                    <Link 
                      to={`/uygulamalar/${app.slug}`}
                      className="app-btn-inspect"
                    >
                      İncele
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
};

export default Applications;
